import React, { useMemo, useState } from 'react';
import { bool, func, number, oneOfType, shape, string } from 'prop-types';
import NumberFormat from 'react-number-format';

import Typography from '@/components/Typography';
import IconMin from '@/assets/Icon/svg/Min';
import IconPlus from '@/assets/Icon/svg/Plus';

import { styInputSpinner } from './style';

const InputSpinner = (props) => {
  const {
    id,
    name,
    className,
    defaultValue,
    decimalScale,
    decimalSeparator,
    disabled,
    error,
    maxValue,
    minValue,
    numStep,
    numThousandSeparator,
    onBlur,
    onChange,
    onEnter,
    onKeyDown,
    placeholder,
    style,
    textHint,
    value: propsValue,
    ...inputProps
  } = props;

  const isControlled = useMemo(() => propsValue !== undefined, [propsValue]);
  const [stateValue, setStateValue] = useState(defaultValue);

  const handleChange = (libEvent) => {
    const event = { ...new Event('change') };
    const newValue = libEvent.floatValue;
    if (maxValue !== null && newValue > maxValue) {
      // If max value is provided and new value is above max value, replace with max value
      event.target = { value: maxValue };
    } else if (minValue !== null && newValue < minValue) {
      // If min value is provided and new value is below min value, replace with min value
      event.target = { value: minValue };
    } else {
      // Otherwise proceed as normal
      event.target = { value: newValue };
    }
    onChange(event);

    if (!isControlled) setStateValue(event.target.value);
  };

  const handleInc = () => {
    const newValue = ((isControlled ? propsValue : stateValue) || 0) + numStep;
    if (maxValue === null || newValue <= maxValue) {
      // Only trigger if new value is below max value or max value is not provided
      if (isControlled) handleChange({ floatValue: newValue });
      else setStateValue(newValue);
    }
  };

  const handleDec = () => {
    const newValue = ((isControlled ? propsValue : stateValue) || 0) - numStep;
    if (minValue === null || newValue >= minValue) {
      // Only trigger if new value is above min value or min value is not provided
      if (isControlled) handleChange({ floatValue: newValue });
      else setStateValue(newValue);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onEnter();
    }
    onKeyDown(event);
  };

  const numberProps = {
    allowNegative: false,
    decimalScale,
    decimalSeparator,
    disabled,
    placeholder,
    readOnly: disabled,
    thousandSeparator: numThousandSeparator,
    isAllowed: ({ floatValue, formattedValue }) =>
      formattedValue === '' ||
      formattedValue[0] === '0' ||
      ((maxValue !== null ? floatValue <= maxValue : true) &&
        (minValue !== null ? floatValue >= minValue : true)),
  };

  const styProps = {
    error,
  };

  const renderMessage = () => {
    switch (true) {
      case !!error:
        // If error exists, show error as highest priority
        return (
          <div>
            <Typography type="alert" alertType="error" style={{ marginTop: 4 }}>
              {error}
            </Typography>
          </div>
        );
      case !!textHint:
        // If no higher priorities present, display textHint if available
        return (
          <div>
            <Typography type="label" style={{ marginTop: 4 }}>
              {textHint}
            </Typography>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div className={styInputSpinner(styProps)}>
        <button
          type="button"
          className="btn-minus"
          disabled={disabled}
          onClick={handleDec}
          data-testid={`btnMin-${id}`}
        >
          <IconMin size={16} />
        </button>
        <NumberFormat
          id={id}
          data-testid={`input-${id}`}
          name={name}
          style={style}
          className={className}
          // Component is always controlled
          // If value is provided, use the provided value. Otherwise, use value stored on state
          value={isControlled ? propsValue : stateValue}
          // Do not pass defaultValue
          onValueChange={handleChange}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          {...inputProps}
          {...numberProps}
        />
        <button
          type="button"
          className="btn-plus"
          disabled={disabled}
          onClick={handleInc}
          data-testid={`btnPlus-${id}`}
        >
          <IconPlus size={16} />
        </button>
      </div>
      {renderMessage()}
    </div>
  );
};

InputSpinner.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  className: string,
  decimalScale: number,
  decimalSeparator: string,
  defaultValue: oneOfType([string, number]),
  disabled: bool,
  error: string,
  maxValue: number,
  minValue: number,
  numStep: number,
  numThousandSeparator: oneOfType([string, bool]),
  onBlur: func,
  onChange: func,
  onEnter: func,
  onKeyDown: func,
  placeholder: string,
  style: shape({}),
  textHint: string,
  value: number,
};

InputSpinner.defaultProps = {
  className: '',
  decimalScale: 0,
  decimalSeparator: ',',
  defaultValue: 0,
  disabled: false,
  error: '',
  maxValue: null,
  minValue: 0,
  numStep: 1,
  numThousandSeparator: '.',
  onBlur: () => {},
  onChange: () => {},
  onEnter: () => {},
  onKeyDown: () => {},
  placeholder: '',
  style: {},
  textHint: '',
  value: undefined,
};

export default InputSpinner;
